import 'reflect-metadata';
import { createRoot } from 'react-dom/client';
import './index.css';
import 'tippy.js/dist/tippy.css';
import './themes/tippy-light-nickels.css';
import reportWebVitals from './reportWebVitals';
import ThemeWrapper from './themes/ThemeWrapper';
import App from './App';
import { bootstrap } from './configuration';
import { ServiceProvider } from '@aesop-fables/containr-react';
import { I18nextProvider } from 'react-i18next';
import { SubmitOnEnter } from './pages/layout/SubmitOnEnter';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/es';
import { isInApp } from './utils/inApp';
import { API_BASE_URL } from './configuration/Environment';
import i18next, { i18nPromise } from './i18n';

// Some flows (e.g. sign-up) rely on these plugins; ensure they're available even when code-splitting
// prevents other screens from importing/extending dayjs first.
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const ensurePreconnect = (href: string) => {
  if (!href) return;
  let origin: string;
  try {
    origin = new URL(href, window.location.href).origin;
  } catch {
    return;
  }
  if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = '';
  document.head.appendChild(link);
};

const ensureDnsPrefetch = (href: string) => {
  if (!href) return;
  let origin: string;
  try {
    origin = new URL(href, window.location.href).origin;
  } catch {
    return;
  }
  // dns-prefetch uses just the origin, and is cheaper than preconnect.
  if (document.head.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'dns-prefetch';
  link.href = origin;
  document.head.appendChild(link);
};

const container = bootstrap();
const root = createRoot(document.getElementById('root') as HTMLElement);
isInApp();

ensureDnsPrefetch(API_BASE_URL);
ensurePreconnect(API_BASE_URL);

// Render immediately. i18next continues initializing via the HTTP backend in the background.
root.render(
  <ServiceProvider rootContainer={container}>
    <I18nextProvider i18n={i18next}>
      <SubmitOnEnter>
        <ThemeWrapper>
          <App />
        </ThemeWrapper>
      </SubmitOnEnter>
    </I18nextProvider>
  </ServiceProvider>
);

i18nPromise.catch((err) => {
  // Best-effort: app will still render using fallback keys/strings, but record the failure for debugging.
  console.warn('[i18n] initialization failed', err);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
